'use client';

import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { Typography } from '@/components/atoms';
import { LinkButton } from '@/components/molecules';
import { usePathname } from '@/i18n/routing';
import { fetchWidgetDetails } from '@/lib/api';
import { logger } from '@/lib/logger';

import { ContentTypeVariations } from '../CardCarousel';
import { ContentCard, ContentCardVariant } from '../ContentCard';

import {
  getPodcastChapterParams,
  toPodcastChapters,
} from '@/components/organisms/Widget/getPodcastChapters';
import { Chapter } from '@/lib/contexts/media-controller/util';
import { Widget, WidgetItem } from '@/lib/api/types';
import { UUID } from '@/types';

type WidgetContentGridProps = {
  siteId: UUID | undefined;
  widgetId: string | undefined | string[];
  initialData?: Widget;
  tenantId: string;
};

const WidgetContentGrid = ({
  siteId,
  widgetId,
  initialData,
  tenantId,
}: WidgetContentGridProps) => {
  const locale = useLocale();
  const t = useTranslations();
  const pathname = usePathname();
  const [data, setData] = useState<Widget | null>(initialData ?? null);

  useEffect(() => {
    if (data) {
      return;
    }

    const fetchData = async () => {
      try {
        const widgetData = await fetchWidgetDetails(
          siteId,
          Number(widgetId),
          tenantId,
          { locale },
        );
        setData(widgetData);
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        logger.error(typedError.message, {
          source: 'nextjs-app-widgetcontentgrid-fetch',
          digest: typedError.digest,
          stack: typedError.stack,
        });
      }
    };

    void fetchData();
  }, [data, siteId, tenantId, widgetId, locale]);

  const rawContentType = data?.content_parameters.content_type;
  const contentType =
    rawContentType === 'podcast_series'
      ? ContentTypeVariations.PODCAST
      : (rawContentType as ContentTypeVariations);

  const isPodcast = contentType === ContentTypeVariations.PODCAST;

  const maxItems = data?.template.configuration?.max_items;
  const allItems = maxItems
    ? data?.template.items?.slice(0, maxItems)
    : data?.template.items;

  const podcastChapters: Chapter[] | undefined = isPodcast
    ? (allItems?.map(item => ({
        id: item.id,
        podcast_id: Number(item.podcast_id),
        image_url: item.image_url ?? '',
        title: item.name ?? item.title,
      })) as Chapter[])
    : undefined;

  const chapterParams = getPodcastChapterParams(data?.template.parameters);
  const episodeChapters = toPodcastChapters(chapterParams);

  return (
    <>
      {data && (
        <>
          <div className="flex items-baseline space-x-4">
            <LinkButton variant="secondary" href={pathname}>
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              {t('B2B_LIBRARY_SEE_ALL_BUTTON_BACK')}
            </LinkButton>
            <Typography.Heading variant="h5" as="h1">
              {data.title}
            </Typography.Heading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {chapterParams.length > 0
              ? chapterParams.map((item, index) => (
                  <ContentCard
                    key={item.id}
                    variant={ContentCardVariant.DETAILED}
                    contentType={ContentTypeVariations.PODCAST}
                    title={item.name}
                    description={item.description}
                    imageUrl={item.image_url ?? ''}
                    className="w-full"
                    slug={item.id.toString()}
                    episodeIndex={index}
                    rawData={{
                      id: String(item.podcast_id),
                      chapters: episodeChapters,
                    }}
                  />
                ))
              : allItems?.map((item: WidgetItem, index: number) => (
                  <ContentCard
                    key={item.id}
                    variant={ContentCardVariant.DETAILED}
                    contentType={contentType}
                    title={item.title}
                    description={item.short_description}
                    imageUrl={item.art_work?.url ?? ''}
                    className="w-full"
                    slug={item.id.toString()}
                    episodeIndex={
                      isPodcast && item.podcast_id ? index : undefined
                    }
                    rawData={
                      isPodcast && item.podcast_id
                        ? {
                            id: String(item.podcast_id),
                            chapters: podcastChapters ?? [],
                          }
                        : undefined
                    }
                  />
                ))}
          </div>
        </>
      )}
    </>
  );
};

export { WidgetContentGrid, type WidgetContentGridProps };
